import React from 'react';
import { Button } from '@appkit4/react-components/button';
import { sanitizeUrl } from '../../../utils/url';

interface DiffReportGeneratorProps {
  originalContent: string[];
  modifiedContent: string[];
  differences: Array<{
    row: number;
    action: '-' | '+';
    text: string;
    range?: [number, number][];
    padding?: boolean;
  }>;
  contextLines?: number;
  disabled?: boolean;
}

interface LineContent {
  text: string;
  lineNumber: number;
  isPlaceholder?: boolean;
}

interface ProcessedContent {
  originalLines: LineContent[];
  modifiedLines: LineContent[];
}

const DiffReportGenerator: React.FC<DiffReportGeneratorProps> = ({
  originalContent,
  modifiedContent,
  differences,
  contextLines = 3,
  disabled = false,
}) => {
  // Updated processContent using maps for both additions and deletions
  const processContent = (): ProcessedContent => {
    // 1. Flatten pages into raw lines
    const rawOriginalLines: string[] = originalContent.flatMap((page) =>
      page.split('\n')
    );
    const rawModifiedLines: string[] = modifiedContent.flatMap((page) =>
      page.split('\n')
    );

    // 2. Create two maps for differences with padding
    //    - For diffs with action '+' (addition) insert a placeholder in the original view.
    //    - For diffs with action '-' (deletion) insert a placeholder in the modified view.
    const additionsMap = new Map<number, typeof differences>();
    const deletionsMap = new Map<number, typeof differences>();

    differences.forEach((diff) => {
      if (diff.padding) {
        if (diff.action === '+') {
          if (!additionsMap.has(diff.row)) {
            additionsMap.set(diff.row, []);
          }
          additionsMap.get(diff.row)!.push(diff);
        } else if (diff.action === '-') {
          if (!deletionsMap.has(diff.row)) {
            deletionsMap.set(diff.row, []);
          }
          deletionsMap.get(diff.row)!.push(diff);
        }
      }
    });

    // 3. Build the final arrays with padding
    const originalLines: LineContent[] = [];
    const modifiedLines: LineContent[] = [];
    let origIndex = 0;
    let modIndex = 0;

    while (origIndex < rawOriginalLines.length || modIndex < rawModifiedLines.length) {
      // Handle additions: if at the current modified index there's an extra line,
      // insert a placeholder in the original view.
      if (modIndex < rawModifiedLines.length && additionsMap.has(modIndex)) {
        originalLines.push({
          text: '',
          lineNumber: origIndex, // assign sequential line number for display
          isPlaceholder: true,
        });
        modifiedLines.push({
          text: rawModifiedLines[modIndex],
          lineNumber: modIndex,
        });
        modIndex++;
        continue;
      }

      // Handle deletions: if at the current original index there's an extra line,
      // insert a placeholder in the modified view.
      if (origIndex < rawOriginalLines.length && deletionsMap.has(origIndex)) {
        originalLines.push({
          text: rawOriginalLines[origIndex],
          lineNumber: origIndex,
        });
        modifiedLines.push({
          text: '',
          lineNumber: modIndex,
          isPlaceholder: true,
        });
        origIndex++;
        continue;
      }

      // Otherwise, add the real lines (if available) to both views.
      if (origIndex < rawOriginalLines.length) {
        originalLines.push({
          text: rawOriginalLines[origIndex],
          lineNumber: origIndex,
        });
        origIndex++;
      }
      if (modIndex < rawModifiedLines.length) {
        modifiedLines.push({
          text: rawModifiedLines[modIndex],
          lineNumber: modIndex,
        });
        modIndex++;
      }
    }

    return { originalLines, modifiedLines };
  };

  // Group differences into context blocks (kept as in your original code)
  const groupDifferences = () => {
    const sortedDiffs = [...differences].sort((a, b) => a.row - b.row);
    const groups: Array<Array<typeof differences[0]>> = [];
    let currentGroup: typeof differences = [];

    sortedDiffs.forEach((diff, index) => {
      if (index === 0) {
        currentGroup.push(diff);
        return;
      }

      const prevDiff = sortedDiffs[index - 1];
      if (diff.row <= prevDiff.row + contextLines * 2) {
        currentGroup.push(diff);
      } else {
        if (currentGroup.length > 0) {
          groups.push([...currentGroup]);
        }
        currentGroup = [diff];
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  };

  // Helper to process inline differences for HTML
  const processLineContent = (
    text: string,
    ranges: [number, number][],
    type: 'added' | 'removed'
  ) => {
    let result = '';
    let lastIndex = 0;
    ranges.forEach(([start, end]) => {
      result += text.slice(lastIndex, start);
      result += `<span class="highlight-${type}">${text.slice(start, end)}</span>`;
      lastIndex = end;
    });
    result += text.slice(lastIndex);
    return result;
  };

  const getPageForLine = (lineNumber: number): number => {
    let currentLine = 0;
    for (let i = 0; i < originalContent.length; i++) {
      const pageLines = originalContent[i].split('\n').length;
      if (currentLine + pageLines > lineNumber) {
        return i + 1;
      }
      currentLine += pageLines;
    }
    return originalContent.length;
  };

  const generateHTMLReport = () => {
    const { originalLines, modifiedLines } = processContent();
    const groups = groupDifferences();

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Document Comparison Report</title>
          <style>
            body {
              font-family: monospace;
              margin: 0;
              padding: 20px;
              background-color: #fff;
              color: #333;
              line-height: 1.5;
            }
            .report-header {
              margin-bottom: 20px;
              font-family: Arial, sans-serif;
            }
            .page-indicator {
              color: #666;
              margin: 24px 0 8px;
              font-size: 12px;
              font-family: Arial, sans-serif;
            }
            .diff-container {
              display: flex;
              flex-direction: column;
              margin-bottom: 20px;
              border: 1px solid #e5e5e5;
              border-radius: 4px;
              background-color: #fafafa;
            }
            .header-row {
              display: flex;
              background-color: #f5f5f5;
              border-bottom: 1px solid #e5e5e5;
            }
            .column-header {
              flex: 1;
              padding: 8px 12px;
              font-weight: bold;
              color: #666;
              font-size: 12px;
              font-family: Arial, sans-serif;
            }
            .columns-container {
              display: flex;
            }
            .diff-column {
              flex: 1;
              min-width: 0;
            }
            .diff-column:first-child {
              border-right: 1px solid #e5e5e5;
            }
            .line {
              display: flex;
              min-height: 24px;
              position: relative;
            }
            .line-number {
              flex: 0 0 50px;
              padding: 0 10px;
              color: #666;
              text-align: right;
              user-select: none;
              border-right: 1px solid #e5e5e5;
              background-color: #f5f5f5;
            }
            .line-content {
              flex: 1;
              padding: 0 10px 0 30px;
              white-space: pre-wrap;
              word-wrap: break-word;
              position: relative;
            }
            .placeholder {
              background-color: rgba(0, 0, 0, 0.05);
              min-height: 24px;
            }
            .removed-light {
              background-color: rgba(224, 48, 30, 0.1);
            }
            .removed-light::before {
              content: '•';
              position: absolute;
              left: 10px;
              color: #E0301E;
            }
            .added-light {
              background-color: rgba(78, 181, 35, 0.1);
            }
            .added-light::before {
              content: '•';
              position: absolute;
              left: 10px;
              color: #4EB523;
            }
            .removed {
              background-color: rgba(224, 48, 30, 0.8);
            }
            .removed::before {
              content: '-';
              position: absolute;
              left: 10px;
              color: #E0301E;
            }
            .added {
              background-color: rgba(78, 181, 35, 0.8);
            }
            .added::before {
              content: '+';
              position: absolute;
              left: 10px;
              color: #4EB523;
            }
            .highlight-removed {
              background-color: rgba(224, 48, 30, 0.8);
              border-radius: 2px;
              padding: 0 1px;
            }
            .highlight-added {
              background-color: rgba(78, 181, 35, 0.8);
              border-radius: 2px;
              padding: 0 1px;
            }
          </style>
        </head>
        <body>
          <div class="report-header">
            <h2>Document Comparison Report</h2>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
          ${groups
            .map((group) => {
              const startLine = Math.max(
                0,
                Math.min(...group.map((d) => d.row)) - contextLines
              );
              const endLine = Math.min(
                Math.max(...group.map((d) => d.row)) + contextLines,
                originalLines.length - 1
              );
              const pageNumber = getPageForLine(group[0].row);

              return `
              <div class="page-indicator">Page ${pageNumber}</div>
              <div class="diff-container">
                <div class="header-row">
                  <div class="column-header">Original</div>
                  <div class="column-header">Modified</div>
                </div>
                <div class="columns-container">
                  <div class="diff-column">
                    ${originalLines
                      .slice(startLine, endLine + 1)
                      .map((line) => {
                        if (line.isPlaceholder) {
                          return `
                          <div class="line">
                            <span class="line-number"></span>
                            <span class="line-content placeholder"></span>
                          </div>
                        `;
                        }
                        const diff = group.find(
                          (d) => d.row === line.lineNumber && d.action === '-'
                        );
                        return `
                          <div class="line">
                            <span class="line-number">${line.lineNumber}</span>
                            <span class="line-content ${
                              diff
                                ? diff.range
                                  ? 'removed-light'
                                  : 'removed'
                                : ''
                            }">${
                          diff && diff.range
                            ? processLineContent(line.text, diff.range, 'removed')
                            : line.text
                        }</span>
                          </div>
                        `;
                      })
                      .join('')}
                  </div>
                  <div class="diff-column">
                    ${modifiedLines
                      .slice(startLine, endLine + 1)
                      .map((line) => {
                        const diff = group.find(
                          (d) => d.row === line.lineNumber && d.action === '+'
                        );
                        return `
                          <div class="line">
                            <span class="line-number">${line.lineNumber}</span>
                            <span class="line-content ${
                              diff
                                ? diff.range
                                  ? 'added-light'
                                  : 'added'
                                : ''
                            }">${
                          diff && diff.range
                            ? processLineContent(line.text, diff.range, 'added')
                            : line.text
                        }</span>
                          </div>
                        `;
                      })
                      .join('')}
                  </div>
                </div>
              </div>
            `;
            })
            .join('')}
        </body>
      </html>
    `;

    return htmlContent;
  };

  const handleDownload = () => {
    const htmlContent = generateHTMLReport();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeUrl = sanitizeUrl(url, ['blob:']);
    if (!safeUrl) {
      return;
    }
    // Explicit guard: ensures only blob: URLs are assigned to href, preventing XSS (CWE-80)
    if (!safeUrl.startsWith('blob:')) {
      return;
    }
    link.href = encodeURI(safeUrl);
    link.download = `document-comparison-report-${new Date()
      .toISOString()
      .split('T')[0]}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Button
      kind="tertiary"
      onClick={handleDownload}
      style={{ width: '250px', marginTop: '20px' }}
      icon="icon-time-outline"
      disabled={disabled}
    >
      Download Report
    </Button>
  );
};

export default DiffReportGenerator;
